import { BookOpen, CheckCircle2, GraduationCap, MessageCircle, MessageSquare, Star, Users, Wrench } from 'lucide-react';
import { QuestLinks } from '../../../../components/QuestLinks';
import { UserLinks } from '../../../../components/UserLinks';
import type { CompiledQuest, QuestType } from '../../services/questTypes';

interface QuestResultCardProps {
    quest: CompiledQuest;
}

export const QuestResultCard = ({ quest }: QuestResultCardProps) => {
    const getQuestTypeIcon = (type: QuestType): React.ReactNode => {
        const iconClass = "w-5 h-5";
        const icons: Record<QuestType, React.ReactNode> = {
            'chat': <MessageCircle className={iconClass} />,
            'check-in': <CheckCircle2 className={iconClass} />,
            'story': <BookOpen className={iconClass} />,
            'lesson': <GraduationCap className={iconClass} />,
            'community-question': <Users className={iconClass} />,
            'tool': <Wrench className={iconClass} />,
            'feedback': <MessageSquare className={iconClass} />,
            'rate': <Star className={iconClass} />
        };
        return icons[type];
    };

    const getQuestTypeLabel = (type: QuestType): string => {
        const labels: Record<QuestType, string> = {
            'chat': 'Chat',
            'check-in': 'Check-in',
            'story': 'Story',
            'lesson': 'Lesson',
            'community-question': 'Community Question',
            'tool': 'Tool',
            'feedback': 'Feedback',
            'rate': 'Rate'
        };
        return labels[type] || type;
    };

    return (
        <div className="border-2 border-gray-200 rounded-lg p-4 space-y-3 hover:border-gray-300 transition-colors">
            {/* Header */}
            <div className="flex justify-between items-start gap-2">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Day {quest.journeyDay} - #{quest.order}
                    </span>
                    {getQuestTypeIcon(quest.type)}
                    <span className="text-base font-semibold text-gray-900">{getQuestTypeLabel(quest.type)}</span>
                </div>
                <div className="flex items-center gap-1">
                    {quest.isCompleted && (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                    )}
                </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
                {quest.isPrimary && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Primary
                    </span>
                )}
                {quest.isOptional && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Optional
                    </span>
                )}
                {!quest.isOptional && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Required
                    </span>
                )}
                {quest.isPrerequisite && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Prerequisite
                    </span>
                )}
                {quest.isFree && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Free
                    </span>
                )}
            </div>

            {/* Ollie Text */}
            {quest.ollieText && (
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                        Ollie Text
                    </label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {quest.ollieText}
                    </p>
                </div>
            )}

            {/* Pill Text */}
            {quest.pillText && (
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                        Pill Text
                    </label>
                    <p className="text-sm text-gray-900">
                        {quest.pillText}
                    </p>
                </div>
            )}

            {/* Title */}
            {quest.title && (
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                        Title
                    </label>
                    <p className="text-sm text-gray-900">
                        {quest.title}
                    </p>
                </div>
            )}

            {/* Description */}
            {quest.description && (
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                        Description
                    </label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {quest.description}
                    </p>
                </div>
            )}

            {/* Footer with ID and Copy Button */}
            <div className="pt-2 border-t border-gray-200 flex flex-wrap justify-between items-center gap-2">
                <span className="text-xs text-gray-500 font-mono break-all">
                    ID: {quest.id}
                </span>
                <span className="flex items-center gap-1">
                    <QuestLinks userId={quest.userId} journeyDayId={`day_${quest.journeyDay}`} questId={quest.id} questTemplateId={quest.templateId} />
                </span>
            </div>
            <div className="pt-2 border-t border-gray-200 flex flex-wrap justify-between items-center gap-2">
                <span className="text-xs text-gray-500 font-mono break-all">
                    User ID: {quest.userId}
                </span>
                <UserLinks userId={quest.userId} />
            </div>
        </div>
    );
};

